import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { authService } from '../services'
import { waitlistService, WaitlistQuestion, SurveyResponse } from '../services/waitlistService'
import useBottomNavPadding from '../hooks/useBottomNavPadding'

// Import placeholder images
import waterIcon from '../assets/icons/bathroom.png'
import fruitIcon from '../assets/icons/cart.png'
import snackIcon from '../assets/icons/sofa.png'
import coffeeIcon from '../assets/icons/grocery.png'
import catGrassIcon from '../assets/icons/cat.png'

// Type for upcoming service
type UpcomingService = {
  id: string
  nameKey: string
  icon: string
  descriptionKey: string
  comingSoonKey: string
}

// Available upcoming services
const upcomingServices: UpcomingService[] = [
  {
    id: 'bottled-water',
    nameKey: 'upcomingServices.water.name',
    icon: waterIcon,
    descriptionKey: 'upcomingServices.water.description',
    comingSoonKey: 'upcomingServices.water.comingSoon'
  },
  {
    id: 'coffee-beans',
    nameKey: 'upcomingServices.coffee.name',
    icon: coffeeIcon,
    descriptionKey: 'upcomingServices.coffee.description',
    comingSoonKey: 'upcomingServices.coffee.comingSoon'
  },
  {
    id: 'cat-grass',
    nameKey: 'upcomingServices.catGrass.name',
    icon: catGrassIcon,
    descriptionKey: 'upcomingServices.catGrass.description',
    comingSoonKey: 'upcomingServices.catGrass.comingSoon'
  },
  {
    id: 'snack-box',
    nameKey: 'upcomingServices.snack.name',
    icon: snackIcon,
    descriptionKey: 'upcomingServices.snack.description',
    comingSoonKey: 'upcomingServices.snack.comingSoon'
  }
]

export default function UpcomingServices() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [waitlistStatus, setWaitlistStatus] = useState<Record<string, boolean>>({})
  const [expandedServiceId, setExpandedServiceId] = useState<string | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [currentUser, setCurrentUser] = useState<any>(null)
  const [questions, setQuestions] = useState<Record<string, WaitlistQuestion[]>>({})
  const [responses, setResponses] = useState<Record<string, Record<string, any>>>({})
  const [submitting, setSubmitting] = useState<boolean>(false)

  // Apply bottom padding for the navigation
  useBottomNavPadding()

  // Check authentication and get user data
  useEffect(() => {
    const checkAuth = async () => {
      setIsLoading(true)
      
      try {
        if (!authService.isAuthenticated()) {
          setIsAuthenticated(false)
          setIsLoading(false)
          return
        }
        
        setIsAuthenticated(true)
        const userData = await authService.getCurrentUser()
        setCurrentUser(userData)
        
        // Check waitlist status for all services
        await Promise.all(
          upcomingServices.map(async (service) => {
            try {
              const { hasJoined } = await waitlistService.checkWaitlistStatus(service.id)
              setWaitlistStatus(prev => ({ ...prev, [service.id]: hasJoined }))
            } catch (error) {
              console.error(`Error checking waitlist status for ${service.id}:`, error)
            }
          })
        )
      } catch (error) {
        console.error('Error checking authentication:', error)
      } finally {
        setIsLoading(false)
      }
    }
    
    checkAuth()
  }, [])

  // Toggle waitlist form for a service
  const toggleWaitlist = async (serviceId: string) => {
    // If already on waitlist, do nothing
    if (waitlistStatus[serviceId]) return
    
    // If not authenticated, redirect to login
    if (!isAuthenticated) {
      toast.error(t('upcomingServices.messages.signInToJoin'))
      navigate('/login', { state: { returnTo: '/upcoming-services' } })
      return
    }
    
    // Close any other expanded service
    if (expandedServiceId && expandedServiceId !== serviceId) {
      setExpandedServiceId(null)
    }
    
    // Toggle the current service
    setExpandedServiceId(expandedServiceId === serviceId ? null : serviceId)
    
    // If expanding and no questions loaded yet, load them
    if (expandedServiceId !== serviceId && !questions[serviceId]) {
      try {
        const response = await waitlistService.getSurveyQuestions(serviceId)
        setQuestions(prev => ({ ...prev, [serviceId]: response.questions }))
        
        // Initialize responses object for this service
        const initialResponses: Record<string, any> = {}
        
        // If user has previous responses, use them
        if (response.userResponses.length > 0) {
          response.userResponses.forEach(resp => {
            initialResponses[resp.question_id] = resp.response
          })
        }
        
        setResponses(prev => ({ ...prev, [serviceId]: initialResponses }))
      } catch (error) {
        console.error(`Error loading survey questions for ${serviceId}:`, error)
        toast.error(t('upcomingServices.messages.unableToLoadQuestions'))
      }
    }
  }

  // Handle question response change
  const handleResponseChange = (
    serviceId: string, 
    questionId: string, 
    value: any, 
    questionType: 'text' | 'multiple_choice' | 'single_choice'
  ) => {
    // For multiple choice, we need to handle differently
    if (questionType === 'multiple_choice') {
      const currentResponses = responses[serviceId]?.[questionId] || []
      let newValues
      
      if (Array.isArray(currentResponses)) {
        // Check if value is already selected
        if (currentResponses.includes(value)) {
          // Remove it
          newValues = currentResponses.filter(v => v !== value)
        } else {
          // Add it
          newValues = [...currentResponses, value]
        }
      } else {
        // Initialize as array with current value
        newValues = [value]
      }
      
      setResponses(prev => ({
        ...prev,
        [serviceId]: {
          ...(prev[serviceId] || {}),
          [questionId]: newValues
        }
      }))
    } else {
      // For text and single choice, simply set the value
      setResponses(prev => ({
        ...prev,
        [serviceId]: {
          ...(prev[serviceId] || {}),
          [questionId]: value
        }
      }))
    }
  }

  // Join waitlist for a service
  const handleJoinWaitlist = async (serviceId: string) => {
    if (!isAuthenticated) {
      toast.error(t('upcomingServices.messages.signInToJoin'))
      navigate('/login', { state: { returnTo: '/upcoming-services' } })
      return
    }
    
    // Check if we have required responses
    const serviceQuestions = questions[serviceId] || []
    const serviceResponses = responses[serviceId] || {}
    
    const missingRequired = serviceQuestions
      .filter(q => q.required)
      .filter(q => {
        // Check if the question has been answered
        const response = serviceResponses[q.id]
        if (response === undefined || response === null) return true
        if (Array.isArray(response) && response.length === 0) return true
        if (response === '') return true
        return false
      })
    
    if (missingRequired.length > 0) {
      toast.error(t('upcomingServices.messages.answerRequiredQuestions'))
      return
    }
    
    setSubmitting(true)
    
    try {
      // Prepare survey responses
      const surveyResponses: SurveyResponse[] = Object.entries(serviceResponses).map(
        ([questionId, response]) => ({
          questionId,
          response
        })
      )
      
      // Send request to join waitlist
      await waitlistService.joinWaitlist({
        serviceId,
        surveyResponses
      })
      
      // Update waitlist status
      setWaitlistStatus({ ...waitlistStatus, [serviceId]: true })
      toast.success(t('upcomingServices.messages.joinedWaitlistSuccess', { service: t(upcomingServices.find(s => s.id === serviceId)?.nameKey || '') }))
      setExpandedServiceId(null)
    } catch (error) {
      console.error(`Error joining waitlist for ${serviceId}:`, error)
      toast.error(t('upcomingServices.messages.unableToJoinWaitlist'))
    } finally {
      setSubmitting(false)
    }
  }

  // Render a survey question
  const renderQuestion = (serviceId: string, question: WaitlistQuestion) => {
    const value = responses[serviceId]?.[question.id]
    
    switch (question.question_type) {
      case 'text':
        return (
          <div key={question.id} className="mb-4">
            <label className="block text-gray-700 text-sm font-medium mb-2">
              {question.question}
              {question.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="text"
              value={value || ''}
              onChange={(e) => handleResponseChange(serviceId, question.id, e.target.value, 'text')}
              className="w-full p-3 border border-gray-300 rounded-lg"
              placeholder={t('common.yourAnswer')}
            />
          </div>
        )
        
      case 'single_choice':
        return (
          <div key={question.id} className="mb-4">
            <p className="text-gray-700 text-sm font-medium mb-2">
              {question.question}
              {question.required && <span className="text-red-500 ml-1">*</span>}
            </p>
            <div className="space-y-2">
              {question.options?.map(option => (
                <label key={option.id} className="flex items-start">
                  <input
                    type="radio"
                    name={`question-${question.id}`}
                    checked={value === option.id}
                    onChange={() => handleResponseChange(serviceId, question.id, option.id, 'single_choice')}
                    className="mt-1 mr-2"
                  />
                  <span className="text-sm">{option.text}</span>
                </label>
              ))}
            </div>
          </div>
        )
        
      case 'multiple_choice':
        return (
          <div key={question.id} className="mb-4">
            <p className="text-gray-700 text-sm font-medium mb-2">
              {question.question}
              {question.required && <span className="text-red-500 ml-1">*</span>}
            </p>
            <div className="space-y-2">
              {question.options?.map(option => (
                <label key={option.id} className="flex items-start">
                  <input
                    type="checkbox"
                    checked={Array.isArray(value) && value.includes(option.id)}
                    onChange={() => handleResponseChange(serviceId, question.id, option.id, 'multiple_choice')}
                    className="mt-1 mr-2"
                  />
                  <span className="text-sm">{option.text}</span>
                </label>
              ))}
            </div>
          </div>
        )
        
      default:
        return null
    }
  }

  return (
    <main className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-yellow-400 p-6">
        <div className="flex items-center mb-4">
          <button 
            onClick={() => navigate(-1)}
            className="text-[#262626]"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-[#262626] text-xl font-semibold ml-4">{t('upcomingServices.title')}</h1>
        </div>
        <p className="text-[#262626] mt-2">
          {t('upcomingServices.introduction')}
        </p>
      </div>

      {/* Services List */}
      <div className="flex-1 p-6">
        {isLoading ? (
          <div className="flex justify-center items-center h-40">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-yellow-400"></div>
          </div>
        ) : (
          <div className="space-y-6">
            {upcomingServices.map((service) => (
              <div 
                key={service.id}
                className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden"
              >
                <div className="p-5">
                  <div className="flex items-center mb-3">
                    <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mr-4">
                      <img src={service.icon} alt={t(service.nameKey)} className="w-7 h-7 object-contain" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-[#262626]">{t(service.nameKey)}</h3>
                      <p className="text-xs text-yellow-600 font-medium">{t(service.comingSoonKey)}</p>
                    </div>
                  </div>
                  
                  <p className="text-gray-600 mb-4">{t(service.descriptionKey)}</p>
                  
                  {waitlistStatus[service.id] ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                      <p className="text-green-700 text-sm font-medium">
                        {t('upcomingServices.onWaitlist')}
                      </p>
                    </div>
                  ) : (
                    <>
                      <button
                        onClick={() => toggleWaitlist(service.id)}
                        className="w-full py-2.5 px-4 rounded-lg bg-yellow-400 text-[#262626] font-medium"
                      >
                        {t('upcomingServices.joinWaitlist')}
                      </button>
                      
                      {expandedServiceId === service.id && (
                        <div className="mt-4 border border-gray-200 rounded-lg p-4">
                          {!isAuthenticated ? (
                            <>
                              <p className="text-sm text-gray-600 mb-3">
                                {t('upcomingServices.signInPrompt', { service: t(service.nameKey) })}
                              </p>
                              <button
                                onClick={() => navigate('/login', { state: { returnTo: '/upcoming-services' } })}
                                className="w-full py-2.5 px-4 rounded-lg bg-yellow-400 text-[#262626] font-medium"
                              >
                                {t('common.signIn')}
                              </button>
                            </>
                          ) : (
                            <>
                              <p className="text-sm text-gray-600 mb-3">
                                {t('upcomingServices.surveyPrompt', { service: t(service.nameKey) })}
                              </p>
                              
                              {/* Survey questions */}
                              {questions[service.id]?.length > 0 ? (
                                <div className="mt-4">
                                  {questions[service.id].map(question => renderQuestion(service.id, question))}
                                </div>
                              ) : (
                                <div className="flex justify-center items-center h-16">
                                  <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-yellow-400"></div>
                                </div>
                              )}
                              
                              <button
                                onClick={() => handleJoinWaitlist(service.id)}
                                disabled={submitting}
                                className="w-full py-2.5 px-4 rounded-lg bg-yellow-400 text-[#262626] font-medium mt-4"
                              >
                                {submitting ? t('common.submitting') : t('common.submit')}
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-8 text-center">
          <p className="text-gray-500 text-sm">
            {t('upcomingServices.cantWait')}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mt-4 justify-center">
            <button
              onClick={() => navigate('/flowers')}
              className="py-2.5 px-6 rounded-full border border-yellow-400 text-yellow-600 font-medium"
            >
              {t('upcomingServices.viewFlowerSubscription')}
            </button>
            <button
              onClick={() => navigate('/fruits')}
              className="py-2.5 px-6 rounded-full border border-yellow-400 text-yellow-600 font-medium"
            >
              {t('fruits.fruitSubscription')}
            </button>
          </div>
        </div>
      </div>
    </main>
  )
} 